// Chooses and blends the base cat's clips from what the cat is doing.
//
// Running: idle, walk, trot and gallop are chosen from the cat's speed along the track and kept on
// one shared stride phase, so blends between gaits keep the footfalls together. The phase advances
// by speed / stride, so planted paws stay planted. The race is fast for a 0.5 m cat (about 5 m/s on
// average), so above the clip's own speed the gallop also lengthens its stride a little, as real
// cats do with a longer flight phase, instead of only spinning its legs faster.
//
// Everything else (eating, the trip's float, the landing stagger, celebrating, and settling after
// the finish) cross-fades to its own clip.
import { type AnimationAction, type AnimationClip, type AnimationMixer, LoopOnce, LoopRepeat } from 'three';
import type { ClipInfo } from './catFactory';

export type Gait = 'idle' | 'walk' | 'trot' | 'gallop';
export type CatMode = 'run' | 'eat' | 'float' | 'land' | 'celebrate' | 'settle';
export type SettleClip = 'sit' | 'groom' | 'idle';

/** Speeds (m/s) where the gait changes going up; going down each switches HYSTERESIS lower. */
export const GAIT_UP: Record<Exclude<Gait, 'idle'>, number> = { walk: 0.12, trot: 0.8, gallop: 1.8 };
export const HYSTERESIS = 0.08;

/** The gait for a speed, with hysteresis so a cat near a threshold does not flicker. Pure. */
export function gaitForSpeed(speed: number, current: Gait): Gait {
  const order: Gait[] = ['idle', 'walk', 'trot', 'gallop'];
  const ci = order.indexOf(current);
  const up = (g: Exclude<Gait, 'idle'>) => GAIT_UP[g];
  let target: Gait = 'idle';
  if (speed >= up('gallop')) target = 'gallop';
  else if (speed >= up('trot')) target = 'trot';
  else if (speed >= up('walk')) target = 'walk';
  const ti = order.indexOf(target);
  if (ti < ci) {
    // only step down once clearly below the current gait's threshold
    const threshold = up(current as Exclude<Gait, 'idle'>) - HYSTERESIS;
    if (speed >= threshold) return current;
  }
  return target;
}

/**
 * Effective stride (metres per cycle) for a gait at a speed. Above the clip's speed the gallop
 * stretches by up to 45%, reached at 7 m/s. Pure.
 */
export function strideFor(gait: Gait, speed: number, baseStride: number, scale: number): number {
  let s = baseStride * scale;
  if (gait === 'gallop') {
    const k = Math.min(1, Math.max(0, (speed - 2.6) / (7 - 2.6)));
    s *= 1 + 0.45 * k * k * (3 - 2 * k);
  }
  return s;
}

/** Stride cycles per second for a gait at a speed. Pure. */
export function cycleRate(gait: Gait, speed: number, baseStride: number, scale: number): number {
  if (gait === 'idle') return 0;
  return Math.max(0, speed) / strideFor(gait, speed, baseStride, scale);
}

const FADE = 0.28; // seconds for a cross-fade
const GAITS: Exclude<Gait, 'idle'>[] = ['walk', 'trot', 'gallop'];
const OTHER = ['idle', 'sit', 'groom', 'eat', 'float', 'land_stagger', 'celebrate'] as const;

export interface AnimatorInput {
  mode: CatMode;
  /** m/s along the track. */
  speed: number;
  settle?: SettleClip;
}

export class CatAnimator {
  readonly actions = new Map<string, AnimationAction>();
  private weights = new Map<string, number>();
  private gait: Gait = 'idle';
  private phase = 0;
  private mode: CatMode = 'settle';
  private landing = 0;

  /**
   * `strideScale` is the cat's leg length × size: longer legs cover more ground per stride.
   * `energy` (0..1) from the personality adds a little eagerness to the cadence.
   */
  constructor(private mixer: AnimationMixer, clips: Map<string, AnimationClip>, private info: Record<string, ClipInfo>,
    private strideScale: number, private energy: number, seed: number) {
    for (const [name, clip] of clips) {
      const a = mixer.clipAction(clip);
      a.setLoop(name === 'land_stagger' ? LoopOnce : LoopRepeat, Infinity);
      a.clampWhenFinished = name === 'land_stagger';
      a.enabled = true;
      a.setEffectiveWeight(0);
      a.play();
      // de-synchronise idles between cats
      a.time = (seed * 0.613 % 1) * clip.duration;
      this.actions.set(name, a);
      this.weights.set(name, 0);
    }
    this.weights.set('idle', 1);
    this.actions.get('idle')?.setEffectiveWeight(1);
    this.phase = (seed * 0.37) % 1;
  }

  get currentGait(): Gait { return this.gait; }
  get currentMode(): CatMode { return this.mode; }
  /** 0..1 position in the current stride. */
  get stridePhase(): number { return this.phase; }

  update(dt: number, input: AnimatorInput): void {
    if (input.mode === 'land' && this.mode !== 'land') {
      const a = this.actions.get('land_stagger');
      if (a) { a.reset(); a.play(); }
      this.landing = 0;
    }
    this.mode = input.mode;
    const target = new Map<string, number>();

    if (this.mode === 'run') {
      this.gait = gaitForSpeed(input.speed, this.gait);
      if (this.gait === 'idle') target.set('idle', 1);
      else target.set(this.gait, 1);
    } else if (this.mode === 'eat') target.set('eat', 1);
    else if (this.mode === 'float') target.set('float', 1);
    else if (this.mode === 'celebrate') target.set('celebrate', 1);
    else if (this.mode === 'land') {
      this.landing += dt;
      target.set('land_stagger', 1);
    } else target.set(input.settle ?? 'idle', 1);

    // stride phase shared by every gait; rate from the gait being faded to
    const g = this.gait === 'idle' ? 'walk' : this.gait;
    const stride = this.info[g]?.stride ?? 0.5;
    const cadence = 1 + 0.06 * (this.energy - 0.7);
    if (this.mode === 'run') this.phase = (this.phase + dt * cycleRate(this.gait, input.speed, stride, this.strideScale) * cadence) % 1;
    for (const name of GAITS) {
      const a = this.actions.get(name);
      if (!a) continue;
      a.timeScale = 0; // the phase drives it
      a.time = this.phase * a.getClip().duration;
    }

    // cross-fade weights
    const k = Math.min(1, dt / FADE);
    let total = 0;
    for (const name of [...GAITS, ...OTHER]) {
      const w0 = this.weights.get(name) ?? 0;
      const w = w0 + ((target.get(name) ?? 0) - w0) * k;
      this.weights.set(name, w < 1e-3 ? 0 : w);
      total += w;
    }
    for (const name of [...GAITS, ...OTHER]) {
      const a = this.actions.get(name);
      if (!a) continue;
      const w = (this.weights.get(name) ?? 0) / (total || 1);
      a.setEffectiveWeight(w);
      if (!(GAITS as string[]).includes(name)) a.timeScale = name === 'eat' ? 1 + 0.15 * this.energy : 1;
    }
    this.mixer.update(dt);
  }

  /** True once the landing stagger has played through. */
  get landingDone(): boolean {
    const info = this.info.land_stagger;
    return this.landing >= (info?.duration ?? 1.5) - FADE;
  }

  /** Jumps straight to a pose (no fade), for previews and the first frame. */
  snap(input: AnimatorInput, t = 0): void {
    this.update(0, input);
    for (const a of this.actions.values()) a.setEffectiveWeight(0);
    const key = input.mode === 'run' ? (this.gait === 'idle' ? 'idle' : this.gait)
      : input.mode === 'eat' ? 'eat' : input.mode === 'float' ? 'float' : input.mode === 'celebrate' ? 'celebrate'
        : input.mode === 'land' ? 'land_stagger' : (input.settle ?? 'idle');
    for (const name of this.weights.keys()) this.weights.set(name, name === key ? 1 : 0);
    const a = this.actions.get(key);
    if (a) {
      a.setEffectiveWeight(1);
      if ((GAITS as string[]).includes(key)) { this.phase = (t / a.getClip().duration) % 1; a.time = t % a.getClip().duration; }
      else a.time = t;
    }
    this.mixer.update(0);
  }
}
